import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  RouterProvider,
} from '@tanstack/react-router';
import { Layout, PageLayout } from '@city-sage/aurum-avis-labs-components';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
  RepositoryProvider,
  isNativePlatform,
} from '@city-sage/aurum-avis-labs-components';
import { lazy, Suspense, useEffect } from 'react';
import { Skeleton } from '@city-sage/aurum-avis-labs-components';
import { LoginProvider } from '@city-sage/aurum-avis-labs-components';
import { Sonner, TooltipProvider } from '@city-sage/aurum-avis-labs-components';
import { CookieBanner } from '@city-sage/aurum-avis-labs-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@city-sage/aurum-avis-labs-components';
import { AccountNameGuard } from './components/AccountNameGuard';
import { z } from 'zod';
import { ChatProvider } from './components/chat/ChatProvider';
import { iapService } from './lib/iap-service';
import {
  blogManifest,
  getBlogPost,
  LEGACY_BLOG_SLUG,
  resolveBlogSlug,
} from './lib/blog';
import {
  setAuthenticatedUserContext,
  clearAuthenticatedUserContext,
  useAuth,
  useIsAuthenticated,
} from '@city-sage/aurum-avis-labs-components';

// ===== LANDING PAGE (Only critical page preloaded) =====
import LandingPage from './components/pages/LandingPage';

// ===== PUBLIC ROUTES (Lazy loaded for optimal bundle size) =====
const PublicProfilePage = lazy(
  () => import('./components/pages/PublicProfilePage')
);
const HelpPage = lazy(() => import('./components/pages/HelpPage'));
const BlogPage = lazy(() => import('./components/pages/BlogPage'));
const BlogPostPage = lazy(() => import('./components/pages/BlogPostPage'));
const BlogAiTravelItineraryPlannerPage = lazy(
  () => import('./components/pages/BlogAiTravelItineraryPlannerPage')
);
const TermsPage = lazy(() => import('./components/pages/TermsPage'));
const PrivacyPage = lazy(() => import('./components/pages/PrivacyPage'));
const ItineraryPage = lazy(() => import('./components/pages/ItineraryPage'));
const DayPage = lazy(() => import('./components/pages/DayPage'));
const DayEditPage = lazy(() => import('./components/pages/DayEditPage'));
const SearchPage = lazy(() =>
  import('./components/pages/SearchPage').then((m) => ({
    default: m.SearchPage,
  }))
);
const AllItinerariesPage = lazy(
  () => import('./components/pages/AllItinerariesPage')
);
const ItineraryReelContainer = lazy(() =>
  import('./components/public-itinerary/reel/ItineraryReelContainer').then(
    (m) => ({ default: m.ItineraryReelContainer })
  )
);

// ===== PRIVATE/AUTHENTICATED ROUTES (Lazy loaded) =====
const ProfilePage = lazy(() => import('./components/pages/ProfilePage'));
const CreateItineraryPage = lazy(() =>
  import('./components/pages/CreateItineraryPage').then((m) => ({
    default: m.CreateItineraryPage,
  }))
);
const LegFormPage = lazy(() =>
  import('./components/pages/LegFormPage').then((m) => ({
    default: m.LegFormPage,
  }))
);
const ItineraryEditPage = lazy(
  () => import('./components/pages/ItineraryEditPage')
);
const StopPage = lazy(() => import('./components/pages/StopPage'));
const StopEditPage = lazy(() => import('./components/pages/StopEditPage'));
const CreateManualItineraryPage = lazy(() =>
  import('./components/pages/CreateManualItineraryPage').then((m) => ({
    default: m.CreateManualItineraryPage,
  }))
);

// Loading component for authenticated routes
const AuthRouteLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="space-y-4 w-full max-w-md">
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  </div>
);

interface AppProps {
  msalInstance: PublicClientApplication;
}

// Create a wrapper component that includes ChatProvider and Layout
function AppLayout() {
  return (
    <TooltipProvider>
      <LoginProvider>
        <QueryClientProvider client={queryClient}>
          <RepositoryProvider>
            <ChatProvider>
              <AccountNameGuard>
                <Layout />
              </AccountNameGuard>
            </ChatProvider>
          </RepositoryProvider>
        </QueryClientProvider>
      </LoginProvider>
    </TooltipProvider>
  );
}

// Helper function to wrap components with PageLayout
function withPageLayout(Component: React.ComponentType<any>) {
  return function WrappedComponent(props: any) {
    return (
      <PageLayout>
        <Component {...props} />
      </PageLayout>
    );
  };
}

// Helper function to wrap components with scroll-to-top behavior
function withScrollToTop(Component: React.ComponentType<any>) {
  return function WrappedComponent(props: any) {
    useEffect(() => {
      // Scroll the main element (our scroll container) instead of window
      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, []);
    return <Component {...props} />;
  };
}

// Helper function to wrap authenticated components with PageLayout and Suspense
function withAuthPageLayout(Component: React.ComponentType<any>) {
  return function WrappedComponent(props: any) {
    return (
      <PageLayout>
        <Suspense fallback={<AuthRouteLoading />}>
          <Component {...props} />
        </Suspense>
      </PageLayout>
    );
  };
}

// Define the root route
const rootRoute = createRootRoute({
  component: AppLayout,
  validateSearch: z.object({
    previewItineraryId: z.string().uuid().optional(),
  }),
});

// Landing page - direct child of root (full width)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
  validateSearch: z.object({
    center: z.string().optional(), // JSON stringified center [lng, lat]
    zoom: z.string().optional(), // Zoom level as string
    bounds: z.string().optional(), // Legacy: JSON stringified bounds (for backward compatibility)
  }),
});

// ===== PUBLIC ROUTES (Preloaded, standard PageLayout) =====
const publicProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$userId',
  component: withAuthPageLayout(PublicProfilePage),
});

const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  component: withAuthPageLayout(HelpPage),
});

const blogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog',
  component: withAuthPageLayout(BlogPage),
});

const blogAiTravelItineraryPlannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog/ai-travel-itinerary-planner',
  beforeLoad: () => {
    const target = blogManifest.legacyRedirects[LEGACY_BLOG_SLUG];
    if (target && getBlogPost(target)) {
      throw redirect({ to: '/blog/$slug', params: { slug: target } });
    }
  },
  component: withAuthPageLayout(BlogAiTravelItineraryPlannerPage),
});

const blogPostRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/blog/$slug',
  beforeLoad: ({ params }) => {
    const resolved = resolveBlogSlug(params.slug);
    if (resolved !== params.slug && getBlogPost(resolved)) {
      throw redirect({ to: '/blog/$slug', params: { slug: resolved } });
    }
  },
  component: withAuthPageLayout(() => {
    const { slug } = blogPostRoute.useParams();
    return <BlogPostPage slug={slug} />;
  }),
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: withAuthPageLayout(SearchPage),
});

const allItinerariesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries',
  component: withAuthPageLayout(AllItinerariesPage),
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: withAuthPageLayout(TermsPage),
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/privacy',
  component: withAuthPageLayout(PrivacyPage),
});

const itineraryViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$id',
  component: withAuthPageLayout(withScrollToTop(ItineraryPage)),
});

const dayViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/days/$dayId',
  component: withAuthPageLayout(withScrollToTop(DayPage)),
});

// ===== PRIVATE/AUTHENTICATED ROUTES (Lazy loaded with Suspense) =====
const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: withAuthPageLayout(ProfilePage),
});

const createItineraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/create',
  component: withAuthPageLayout(CreateItineraryPage),
});

const createManualItineraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/create/manual',
  component: withAuthPageLayout(CreateManualItineraryPage),
});

const itineraryEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$id/edit',
  component: withAuthPageLayout(ItineraryEditPage),
});

const dayEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/days/$dayId/edit',
  component: withAuthPageLayout(DayEditPage),
});

const stopViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/stops/$stopId',
  component: withAuthPageLayout(withScrollToTop(StopPage)),
});

const stopEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/stops/$stopId/edit',
  component: withAuthPageLayout(StopEditPage),
});

const stopCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/days/$dayId/stops/new',
  component: withAuthPageLayout(StopEditPage),
});

// Leg routes
const legFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/legs/$legId/form',
  component: withAuthPageLayout(() => {
    const { itineraryId, legId } = legFormRoute.useParams();
    const search = legFormRoute.useSearch();
    return (
      <LegFormPage
        itineraryId={itineraryId}
        fromStopId={search.fromStopId}
        toStopId={search.toStopId}
        legId={legId}
      />
    );
  }),
  validateSearch: z.object({
    fromStopId: z.string(),
    toStopId: z.string(),
  }),
});

const legCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/$itineraryId/legs/form',
  component: withAuthPageLayout(() => {
    const { itineraryId } = legCreateRoute.useParams();
    const search = legCreateRoute.useSearch();
    return (
      <LegFormPage
        itineraryId={itineraryId}
        fromStopId={search.fromStopId}
        toStopId={search.toStopId}
      />
    );
  }),
  validateSearch: z.object({
    fromStopId: z.string(),
    toStopId: z.string(),
  }),
});

// Reel route - full screen like landing page (no PageLayout)
const reelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/itineraries/reels/$startId',
  component: () => {
    const { startId } = reelRoute.useParams();
    const search = reelRoute.useSearch();

    // Parse center and zoom from search params
    const center = search.center ? JSON.parse(search.center) : undefined;
    const zoom = search.zoom ? parseFloat(search.zoom) : undefined;
    const initialActiveIndex = search.activeIndex;

    return (
      <Suspense fallback={<AuthRouteLoading />}>
        <ItineraryReelContainer
          initialItineraryId={startId}
          center={center}
          zoom={zoom}
          initialActiveIndex={initialActiveIndex}
        />
      </Suspense>
    );
  },
  validateSearch: z.object({
    center: z.string().optional(), // JSON stringified center [lng, lat]
    zoom: z.string().optional(), // Zoom level as string
    activeIndex: z.coerce.number().optional(), // Current active story index
  }),
});

// Create the router instance
const router = createRouter({
  routeTree: rootRoute.addChildren([
    // ===== FULL-WIDTH ROUTES (No PageLayout wrapper) =====
    indexRoute, // Landing page - direct child (full width)
    reelRoute, // Reel page - direct child (full width)

    // ===== PUBLIC ROUTES (Preloaded with PageLayout) =====
    publicProfileRoute,
    helpRoute,
    blogRoute,
    blogAiTravelItineraryPlannerRoute,
    blogPostRoute,
    searchRoute,
    allItinerariesRoute,
    termsRoute,
    privacyRoute,
    itineraryViewRoute,
    dayViewRoute,

    // ===== PRIVATE/AUTHENTICATED ROUTES (Lazy loaded with PageLayout + Suspense) =====
    profileRoute,
    createItineraryRoute,
    createManualItineraryRoute,
    itineraryEditRoute,
    dayEditRoute,
    stopCreateRoute,
    stopViewRoute,
    stopEditRoute,
    legFormRoute,
    legCreateRoute,
  ]),
  scrollRestoration: true,
  // Target the main element which is our scroll container
  scrollToTopSelectors: ['main'],
});

const queryClient = new QueryClient();

// Component to handle Application Insights user identification
function AppInsightsUserIdentifier() {
  const { userEmail } = useAuth();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (isAuthenticated && userEmail) {
      // Identify user with Application Insights using email as the unique identifier
      setAuthenticatedUserContext(userEmail);
      console.log(
        '[AppInsights] User identified:',
        userEmail.substring(0, 3) + '***'
      );
    } else if (!isAuthenticated) {
      // Clear user context when logged out
      clearAuthenticatedUserContext();
      console.log('[AppInsights] User context cleared');
    }
  }, [isAuthenticated, userEmail]);

  return null;
}

function App({ msalInstance }: AppProps) {
  // Detect if running in Capacitor (native mobile)
  const isCapacitor = isNativePlatform();

  // Initialize IAP service on app start (only on native platforms)
  useEffect(() => {
    if (isCapacitor && iapService.isAvailable()) {
      iapService.initialize().catch((error) => {
        console.error('[App] Failed to initialize IAP service:', error);
      });
    }
  }, [isCapacitor]);

  // Only wrap with MsalProvider for web (not Capacitor)
  const appContent = (
    <>
      <Sonner />
      <RouterProvider router={router} />
      <CookieBanner />
    </>
  );

  // Wrap with AuthProvider for all platforms
  // Note: AuthProvider uses MSAL hooks, so it must be inside MsalProvider
  // Even on Capacitor, we provide MsalProvider (with an uninitialized instance)
  // to satisfy the MSAL hooks that AuthProvider calls
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <AppInsightsUserIdentifier />
        {appContent}
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;
